
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSlate } from 'slate-react'
import { useForm, useFieldArray } from 'react-hook-form'
import clsx from 'clsx';
import { ListGroup, ListGroupItem, Collapse, Media, Button, Card, CardHeader, CardBody, CardText, InputGroup, InputGroupAddon, Input, Custom} from 'reactstrap';

import bootbox from 'components/Modals/Bootbox';
import { useEditorState, useEditorActions } from '../../editor-context'
import { scrollToNode } from '../../Canvas/TratoFormat'
import { getApiName, truncate, format } from 'utils'
import useDebounce from 'hooks/useDebounce'
import { useProfileAbility } from 'context/profile'
import { VariableInput } from './Preview'

export function useSearchParticipantVariables(list) {
    const [filteredList, setFilteredList] = useState(list ?? []);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 500)

    const onSearchChange = (evt) => {
        setSearch(evt.target.value)
    }

    useEffect(() => {
        if (debouncedSearch.length) {
            const newParts = list.map(part => {

                return {
                    ...part,
                    variables: part.variables.filter(variable => variable.name?.toLowerCase().startsWith(debouncedSearch?.toLowerCase()))
                }
            })

            setFilteredList(newParts)
        } else {
            setFilteredList(list) 
        }
    }, [debouncedSearch, list])

    return { onSearchChange, search, debouncedSearch, filteredList }
}

export function VariableList() {
    const { t } = useTranslation()
    const { document: doc } = useEditorState()
    const { getWordonlineVariables } = useEditorActions()
    const profileAbility = useProfileAbility();
    const { filteredList, search, onSearchChange } = useSearchParticipantVariables(doc.participants);

    const canImportFromExcel = profileAbility.can('IMPORT_VARIABLES_FROM_EXCEL'); // can IMPORT_VARIABLES_FROM_EXCEL
    const canImportFromWord = profileAbility.can('READ_VARIABLES_FROM_WORD'); // can READ_VARIABLES_FROM_WORD
    const canSearchVariablesInContract = profileAbility.can('SEARCH_VARIABLES_IN_CONTRACT'); // can SEARCH_VARIABLES_IN_CONTRACT
    const wordonline = doc.wordonline 

    const handleReadVariablesFromWord = () => {
        getWordonlineVariables()
    }


    return (
        <div >
            <InputGroup className="rounded-0 bd-secondary p-2">
                <InputGroupAddon addonType="prepend">
                    <Button
                        color="success"
                        className={clsx('btn-icon-only', !canImportFromExcel && 'not-allowed')}
                        title={canImportFromExcel ? 'Importar desde Excel' : t('wordList.plans.availablePE')}
                        data-action="importer"
                    >
                        <i className="fas fa-file-excel"></i>
                    </Button>
                </InputGroupAddon>
                {wordonline &&

                    <InputGroupAddon addonType="prepend">
                        <Button
                            color="primary"
                            className={clsx('btn-icon-only', !canImportFromWord && 'not-allowed')}
                            title={canImportFromWord ? 'Leer las variables del documento' : t('wordList.plans.availablePE')}
                            data-action="read"
                            onClick={handleReadVariablesFromWord}
                        >
                            <i className="fas fa-file-word"></i>
                        </Button>
                    </InputGroupAddon>
                }
                <Input
                    type="text"
                    className={clsx('form-control input-sm search-variable', !canSearchVariablesInContract && 'not-allowed')}
                    placeholder={t('editor.variables.search')}
                    title={!canSearchVariablesInContract && t('wordList.plans.availableE')}
                    disabled={!canSearchVariablesInContract}
                    value={search}
                    onChange={onSearchChange}
                />
            </InputGroup>
            <ListGroup>
                {filteredList?.map((participant, i) => {
                    return <ParticipantVariables
                        key={participant._id}
                        participant={participant}
                        documentLanguage={doc.language?.toLowerCase?.()}
                        index={i + 1}
                    />
                })}
            </ListGroup>
        </div>
    )
}


function ParticipantVariables({ participant, index, documentLanguage}) { 
    const { t } = useTranslation()
    const [isOpen, setIsOpen] = useState(false)
    const { updateVariable, addVariable, removeVariable } = useEditorActions()
  
    const toggle = () => setIsOpen(open => !open);

    const handleSave = (variable) => {   
         updateVariable({
            participantId: participant._id,
            variable,
        })
    }
    const handleAdd = () => {   
        addVariable({
            participantId: participant._id,
        })

    }
    const handleDelete = (variable) => {   
       
        removeVariable({ participantId: participant._id, variable })
    }

    return (
        <ListGroupItem className="m-0 p-0 bg-light text-dark">
            <Media className="d-flex justify-content-center align-items-center" onClick={toggle}>
                <Media className={`p-2 mr-2 participant${index}`}>
                    <img src={`/images/editor/icons/participants/${participant.obligation}.svg`} alt={participant.obligation} height="40px" />
                </Media>
                <Media body>
                    <Media>{truncate(participant.label, 25)}</Media>
                    <Media><span className="">{participant.variables.length} {t('variable', { count: participant.variables.length })}</span></Media>
                </Media>
                <Media className="px-2">
                    <i className={`fas fa-chevron-${isOpen ? 'down' : 'up'}`} />
                </Media>
            </Media>
            <Collapse isOpen={isOpen}>
                {participant.variables.map(variable => (
                    <VariableItem variable={variable} hasRequestIneOCR={participant.requestIneOCR} participantIndex={index} documentLanguage={documentLanguage} onSave={handleSave} onDelete={handleDelete} />
                ))}
                {/* {!whoami.contentLocked &&  */}
                <div className="text-center m-1">
                    <Button color="light" data-action="add-variable" size="sm" onClick={handleAdd}>
                        <i className="fas fa-plus"></i> {t('editor.variables.add')}
                    </Button>
                </div>
                {/* } */}
            </Collapse>
        </ListGroupItem>
    )
}


function VariableItem({ variable = {}, participantIndex,  hasRequestIneOCR,documentLanguage, onSave, onDelete }) {
    const { t } = useTranslation()
    const [isEditable, setIsEditable] = useState(!variable.apiName.length);
    const editor = useSlate();

    const toggleEditable = () => setIsEditable(editable => !editable);
    const { register, watch, control, getValues, reset } = useForm({
        defaultValues: {
            name: variable.name,
            value: variable.value,
            type: variable.type,
            ocrProperty: variable.ocrProperty,
            limit: variable.limit,
            isLimitRequired: variable.isLimitRequired,
            options: variable.options.map(o => ({ value: o }))
        }
    });
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'options'
      });

    useEffect(() => {
        reset({
            name: variable.name,
            value: variable.value,
            type: variable.type,
            ocrProperty: variable.ocrProperty,
            limit: variable.limit,
            isLimitRequired: variable.isLimitRequired,
            options: variable.options.map(o => ({ value: o }))
        })
    }, [variable])

    const type = watch('type', variable.type)
    const limit = watch('limit', variable.limit);
    const isLimitRequired = watch('isLimitRequired', variable.isLimitRequired);
    const options = watch('options', variable.options.map(o => ({ value: o })));

    const handleSave = () => {
        const newValues = getValues();
        const apiName = newValues.type === 'ocr:ine' ?  getApiName(newValues['ocrProperty'], participantIndex) : getApiName(newValues.name, participantIndex);
        newValues.value = newValues.value ? newValues.value : '';
        onSave({
            ...variable,
            ...newValues,
            options: newValues.type === 'fixed' ? newValues.options.map(opt => opt.value) : [],
            value: type === 'date' ? format(newValues.dateValue, 'PPP') : newValues.value,
            apiName
        })
        setIsEditable(false)
    }

    const handleAddOption = () => {
        append({ value: '' })
    }
    const handleRemoveOption = (index) => () => {
        remove(index)
    }
    const handleDeleteVariable = async () => {
        const resp = await bootbox.confirm(t('editor.variables.delete_confirm'));
        if (!resp) return;
            
        onDelete(variable)
    }
    const handleClick = () => {
        scrollToNode(editor, variable)
    }

    return (
        <Card onClick={handleClick} className="bg-light">
            <CardHeader className="d-flex align-items-center">
                <div className="w-100">
                    <span>{variable.name}</span><br></br>
                    {variable.type === 'dynamictable' && <small>{t('editor.variables.type_dynamictable')}</small>}
                    {variable.type !== 'dynamictable' && <small>{variable.value || t('editor.variables.new_type_empty')}</small>}
                </div>
                <div  className="d-flex flex-column align-items-center justify-content-center">
                    <span data-action="edit-variable" onClick={toggleEditable} className="pointer">
                        <i className="fas fa-pen"></i>
                    </span>
                 {/* {!whoami.contentLocked &&  */}
                     <span data-action="delete-variable" onClick={handleDeleteVariable} className="pointer">
                         <i className="fas fa-times text-danger"></i>
                     </span>
                 {/* } */}
                 </div>
             </CardHeader>
            <Collapse isOpen={isEditable}>
             <CardBody className="variable-item-editor bg-light-light ">
                <form noValidate>
                    <div className="form-group position-relative">
                        <label>{t('editor.variables.name')}:</label>
                        <input 
                            type="text" 
                            className="form-control input-sm" 
                            placeholder={t('editor.variables.name_placeholder')} 
                            name="name"
                            ref={register}
                        />
                    </div> 
                    {/* {{#can 'SET_VARIABLE_TYPE'}} */}
                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label>{t('editor.variables.type')}:</label>
                                <select className="form-control input-sm" name="type" data-action="update-type" ref={register}>
                                    <option value="text">{t('editor.variables.type_text')}</option>
                                    <option value="textarea">{t('editor.variables.type_paragraph')}</option>
                                    <option value="date">{t('editor.variables.type_date')}</option>
                                    <option value="number">{t('editor.variables.type_number')}</option>
                                    <option value="fixed" >{t('editor.variables.type_fixed')}</option>
                                    <option value="dynamictable">{t('editor.variables.type_dynamictable')}</option>
                                    {hasRequestIneOCR && <option value="ocr:ine">{t('INE')}</option> }
                                </select>
                            </div>
                        </div>
                        <div className="col-md-6">
                            { type === 'text' && 
                                <div className="form-group">
                                    <label>
                                        {t('editor.variables.max_value')}:{' '}
                                        <span title={t('editor.variables.limit_help')}>
                                            <i className="fas fa-question-circle" />
                                        </span>
                                    </label>
                                    <input 
                                        type="number" 
                                        className="form-control input-sm" 
                                        placeholder={t('editor.variables.max_value_placeholder')}
                                        name="limit" 
                                        defaultValue={variable.limit}
                                        ref={register} 
                                    />
                                    <div className="d-flex align-items-center mt-1">
                                        <input 
                                            type="checkbox" 
                                            className="input-sm" 
                                            name="isLimitRequired" 
                                            defaultValue={variable.isLimitRequired}
                                            ref={register} 
                                        />{' '}
                                        <small className="ml-1 text-muted">Obligatorio</small>
                                    </div>
                                </div> 
                            }
                            { type === 'ocr:ine' && 
                                <div className="form-group">
                                    <label>
                                        {t('Propiedad')}:{' '}
                                        <span title={t('editor.variables.limit_help')}>
                                            <i className="fas fa-question-circle" />
                                        </span>
                                    </label>
                                    <select className="form-control input-sm" name="ocrProperty" data-action="update-type" ref={register}>
                                        <option value="curp">{t('CURP')}</option>
                                        <option value="name">{t('Nombre')}</option>
                                        <option value="lastName">{t('Apellido')}</option>
                                        <option value="claveElector">{t('Clave de elector')}</option>
                                        <option value="birthDate">{t('Fecha de nacimiento')}</option>
                                        <option value="gender">{t('Sexo')}</option>
                                        <option value="address">{t('Domicilio')}</option>
                                        {/* <option value="fixed" >{t('editor.variables.type_fixed')}</option>
                                        <option value="dynamictable">{t('editor.variables.type_dynamictable')}</option>
                                        <option value="ocr:ine">{t('INE')}</option> */}
                                    </select>
                                </div> 
                            }
                        </div>
                    </div> 
                    {/* {{/can}} */}
                   {type === 'fixed' && 
                        <div className='variable-item-options'>
                            <div className="form-group d-flex justify-content-between">
                                <label>{t('editor.variables.fixed_option_title')}:</label>
                                <span className="pointer hover_text-decoration-underline" data-action="add-option" onClick={handleAddOption}>
                                    <i className="fas fa-plus" /> {t('editor.variables.fixed_option_add')}
                                </span>
                            </div>
                            {fields.map(({ id, value }, index) => (
                                <div key={id} className="form-group variable-options position-relative d-flex align-items-center">
                                    <input 
                                        type="text" 
                                        className="form-control input-sm" 
                                        placeholder={t('editor.variables.fixed_option_placeholder')}
                                        name={`options[${index}].value` }
                                        defaultValue={value}
                                        data-action="update-option" 
                                        data-index={index}
                                        ref={register()}
                                    />
                                    <span className="p-1 pointer" data-action="remove-option" data-index={index} onClick={handleRemoveOption(index)}>
                                        <i className="text-danger fas fa-minus" />
                                    </span>
                                </div>
                            ))}
                        </div>
                    }
                    
                    {type !== 'dynamictable' && type !== 'ocr:ine' &&
                        <div className="form-group">
                            <label>{t('editor.variables.value')}:</label>
                            <VariableInput variable={{...variable, type, options, limit, isLimitRequired }} documentLanguage={documentLanguage} control={control} />
                            {/* // {type === 'fixed' ? (
                            //     <>
                            //         <select className="form-control input-sm" name="value" data-action="zombie-select" ref={register}>
                            //             <option value="">{t('editor.variables.value_placeholder')}</option>
                            //             {options.map(({ id, value }, index) => (
                            //                 <option key={id} value={value} >{value}</option>
                            //             ))}
                            //         </select>
                            //     </>
                            // ) : (
                            //     <input 
                            //         type="text" 
                            //         className="form-control input-sm" 
                            //         placeholder={t('editor.variables.value_placeholder')}
                            //         name="value" 
                            //         ref={register}
                            //         maxLength={type === 'text' && limit ? limit : 524288} 
                            //         // size={type === 'text' && limit ? limit : 524288}
                            //     />
                            // )} */}
                                
                                
                        </div>
                    }

                {/* {{#is editor 'template'}}
                    {{#or tenant.kissflow tenant.hubspot}}
                    {{#is type 'text'}}
                        <div className="form-group">
                        <label>{{ word 'editor.variables.mapping_value' }}: </label>
                            <a title="{{word 'editor.variables.mapping_help'}}">
                            <i className="fas fa-question-circle"></i>
                            </a>
                            {{#is tenant.hubspot true}}
                            <a className="pull-right hubspot-link" title="Hubspot" id="hubspot">
                            <div className="hubspot-logo"></div>
                            </a>
                            {{/is}}
                            {{#is tenant.kissflow true}}
                            <a className="pull-right" title="Kissflow" id="kissflow">
                            <img className="kissflow-logo" src="/images/editor/icons/kissflow.png" />
                            </a>
                            {{/is}}
                        <input type="text" className="form-control input-sm mappingname" placeholder="{{ word 'editor.variables.mapping_value_placeholder' }}" name="mappingName" value="{{mappingName}}">
                        </div>
                    {{/is}}
                    {{/or}}
                {{/is}} */}

                </form>
                    <div className="text-right">
                        <Button className="text-dark" color="secondary-light" data-action="save-edit" onClick={handleSave}>
                            <i className="fas fa-check"></i> {t('editor.variables.save')}
                        </Button>
                    </div>
                </CardBody>
                </Collapse>
        </Card>
    )
}